import { Controller } from 'react-hook-form';
import { getWorklogStatusText } from '@/features/types';
import { WorklogIcon } from '@/views/map/worklog/WorklogIcon/WorklogIcon';
import { useSmallScreen } from '@/hooks/useWindowDimension';
import { FilterSection } from './FilterSection';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { WorkLogStatus } from '@/generated/types/navigator/types.gen';
import { Checkbox, Flex } from '@mantine/core';
import classes from './Filter.module.css';
import type { ChangeEventHandler } from 'react';
import { useCallback } from 'react';
import type { Control } from 'react-hook-form';
import type { FilterState } from '@/features/filterSlice';

export const WorkStatusFilter = (props: {
  control: Control<FilterState>;
  data: WorkLogStatus[];
  showTitle?: boolean;
}) => {
  const sortedOptions: WorkLogStatus[] =
    props.data.length > 1
      ? [
          ...props.data.filter((s) => s !== WorkLogStatus.NOT_ADDED).sort(),
          WorkLogStatus.NOT_ADDED,
        ]
      : props.data;

  const fieldInfoOptions = sortedOptions?.map((i) => ({
    value: i,
    label: getWorklogStatusText(i),
  }));
  const isSmallScreen = useSmallScreen();
  const intl = useClientTerminology();

  const generateLabel = useCallback(
    (status: WorkLogStatus, text: string) => (
      <Flex gap="xs" align="center">
        {status === WorkLogStatus.NOT_ADDED && isSmallScreen ? (
          <div className={classes.emptySpace}></div>
        ) : status === WorkLogStatus.NOT_ADDED && !isSmallScreen ? null : (
          <WorklogIcon workStatus={status} />
        )}
        {text}
      </Flex>
    ),
    [isSmallScreen]
  );

  const controller = (
    <Controller
      control={props.control}
      name="workStatus"
      render={({ field: { onChange, onBlur, value } }) => {
        const handleBlur = async () => {
          onBlur();
        };
        const handleChange: ChangeEventHandler<HTMLInputElement> = async (
          event
        ) => {
          if (event.target.checked) {
            onChange([...value, event.target.value]);
          } else {
            onChange(value.filter((value) => value !== event.target.value));
          }
        };

        return (
          <div className={classes.checkboxGroup}>
            <span className={classes.heading}>Activity since last scan</span>
            {fieldInfoOptions?.map(
              (
                fieldInfoOption: { label: string; value: WorkLogStatus },
                key: number
              ) => {
                return (
                  <Checkbox
                    data-testid={`work-status-${fieldInfoOption.value}`}
                    key={key}
                    label={generateLabel(
                      fieldInfoOption.value,
                      fieldInfoOption.label
                    )}
                    name={fieldInfoOption.label}
                    value={fieldInfoOption.value}
                    checked={value.includes(fieldInfoOption.value)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    classNames={{
                      root: classes.root,
                      input: classes.input,
                    }}
                    size="md"
                  />
                );
              }
            )}
          </div>
        );
      }}
    />
  );

  if (props.data.length === 0) {
    return null;
  }
  return (
    <FilterSection
      title={
        props.showTitle !== false
          ? intl.formatMessage({ id: 'filter.activitySection' })
          : undefined
      }
    >
      {controller}
    </FilterSection>
  );
};
